export type ToolResult = {
  content: Array<{ type: 'text'; text: string }>
  isError?: boolean
}

export type ToolAnnotations = {
  readOnlyHint?: boolean
  untrustedContentHint?: boolean
}

export type ModelContextTool = {
  name: string
  title?: string
  description: string
  inputSchema?: object
  annotations?: ToolAnnotations
  execute: (input: Record<string, unknown>, options: { signal: AbortSignal }) => Promise<ToolResult>
}

export type RegisteredTool = {
  name: string
  title?: string
  description: string
  inputSchema?: object
  annotations?: ToolAnnotations
}

type ModelContextLike = {
  registerTool: (
    tool: ModelContextTool,
    options?: { signal?: AbortSignal; exposedTo?: string[] },
  ) => Promise<void>
  getTools?: () => Promise<RegisteredTool[]>
  addEventListener?: (type: string, listener: () => void) => void
  removeEventListener?: (type: string, listener: () => void) => void
  // Marque posée uniquement par `LocalWebMCPContext` (voir `webmcp/polyfill.ts`).
  // Un vrai navigateur qui expose `document.modelContext` nativement ne pose
  // jamais cette propriété : elle sert exactement à distinguer « un agent
  // WebMCP réel répond » de « le polyfill de secours répond à sa place »,
  // sans que ce fichier ait besoin d'importer la classe du polyfill (ce qui
  // créerait un cycle, `polyfill.ts` important déjà les types d'ici).
  readonly isLocalPolyfill?: true
}

declare global {
  interface Document {
    modelContext?: ModelContextLike
  }
  interface Navigator {
    modelContext?: ModelContextLike
  }
}

export function getModelContext(): ModelContextLike | null {
  if (typeof document === 'undefined') return null
  const candidate = document.modelContext ?? globalThis.navigator?.modelContext
  return candidate && typeof candidate.registerTool === 'function' ? candidate : null
}

export type Availability =
  | { supported: true; surface: 'document' | 'navigator' }
  | { supported: false; reason: 'no-api' | 'insecure-context' }

export function checkAvailability(): Availability {
  if (typeof document === 'undefined') return { supported: false, reason: 'no-api' }
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return { supported: false, reason: 'insecure-context' }
  }
  // document.modelContext.registerTool is the WebMCP Challenge surface.
  if (document.modelContext?.registerTool) return { supported: true, surface: 'document' }
  if (globalThis.navigator?.modelContext?.registerTool) {
    return { supported: true, surface: 'navigator' }
  }
  return { supported: false, reason: 'no-api' }
}

/**
 * Distingue le polyfill local (`webmcp/polyfill.ts`) d'un vrai support natif.
 *
 * `checkAvailability()` ne rapporte que la SURFACE (`document` ou
 * `navigator`) — volontairement inchangé, un test existant fige sa forme
 * exacte. La marque `isLocalPolyfill`, posée uniquement par
 * `LocalWebMCPContext`, est la seule différence observable entre les deux :
 * un vrai `document.modelContext` natif ne l'a jamais.
 *
 * C'est ce que lit l'écran pour ne jamais afficher le même « WebMCP ready »
 * vert dans les deux cas — un juge sur un Chrome ordinaire, sans agent
 * connecté, ne doit pas croire que le support natif fonctionne.
 */
export function isLocalPolyfillActive(): boolean {
  if (typeof document === 'undefined') return false
  return document.modelContext?.isLocalPolyfill === true
}

export function text(value: string): ToolResult {
  return { content: [{ type: 'text', text: value }] }
}

export function failure(value: string): ToolResult {
  return { content: [{ type: 'text', text: value }], isError: true }
}

export type { ModelContextLike }
